import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { http_get, http_post } from '../../pages/utils/api_client';
import { API_ENDPOINTS } from '../../pages/utils/endpoints';

// BL-067 — Invite Users modal, matching the "Manage Access + Invite (updated)"
// mockup. Two tabs (From audience / By email), invite options, optional message.
// Wires to the existing POST /api/v1/material_invites backend.

const SEGMENTS = [
  { key: 'all', label: 'All' },
  { key: 'customer', label: 'Customer' },
  { key: 'trial', label: 'Trial' },
  { key: 'lead', label: 'Lead' }
];
// BL-082 — first/last name are per-person and belong in CSV import, not a batch
// email invite. The invite only offers the batch-appropriate attributes.
const PER_PERSON_KEYS = ['first_name', 'last_name'];
const SEGMENT_OPTIONS = ['customer', 'trial', 'lead'];
const TG_CLASS = { customer: 'cust', trial: 'trial', lead: 'lead' };
const AV_COLORS = ['#E8843C', '#3B7DD8', '#5B54C0', '#3F8F55', '#C0546B', '#0FA3B8', '#8A6D3B'];

const hashStr = (s) => String(s).split('').reduce((a, c) => (((a << 5) - a) + c.charCodeAt(0)) | 0, 0);
const colorFor = (key) => AV_COLORS[Math.abs(hashStr(key)) % AV_COLORS.length];
const initialsFor = (a) => {
  const f = a.learner?.first_name || '';
  const l = a.learner?.last_name || '';
  const base = (f[0] || '') + (l[0] || '');
  return (base || (a.learner?.name || a.learner?.email || '?')[0]).toUpperCase();
};
const timeAgo = (iso) => {
  if (!iso) return '';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 3600) return 'today';
  const hrs = Math.floor(secs / 3600);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const parseEmails = (text) => {
  const seen = new Set();
  return String(text)
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.includes('@') && !seen.has(e) && seen.add(e));
};

const InviteModal = ({ materials = [], initialMaterialId = null, materialType = 'walkthrough', onClose, onSent }) => {
  const unit = materialType === 'paths' ? 'stages' : 'steps';
  const kindWord = materialType === 'paths' ? 'path' : 'walkthrough';

  const [materialId, setMaterialId] = useState(initialMaterialId || materials[0]?.id || null);
  const [tab, setTab] = useState('audience');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [segment, setSegment] = useState('all');
  const [audiences, setAudiences] = useState([]);
  const [loadingAudiences, setLoadingAudiences] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [emailsText, setEmailsText] = useState('');
  const [alsoAdd, setAlsoAdd] = useState(true);
  const [attrCatalog, setAttrCatalog] = useState({ standard: [], custom: [] });
  const [attrValues, setAttrValues] = useState({});
  const [showAttrs, setShowAttrs] = useState(false);
  const [sendBranded, setSendBranded] = useState(true);
  const [autoRemind, setAutoRemind] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const selectedMaterial = materials.find((m) => m.id === materialId) || materials[0];

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (tab !== 'audience') return undefined;
    let cancelled = false;
    setLoadingAudiences(true);
    http_get(API_ENDPOINTS.GET_AUDIENCES, { keyword: debounced, segment, limit: 50 })
      .then(({ data }) => { if (!cancelled) setAudiences(data.records || []); })
      .catch(() => { if (!cancelled) setAudiences([]); })
      .finally(() => { if (!cancelled) setLoadingAudiences(false); });
    return () => { cancelled = true; };
  }, [tab, debounced, segment]);

  // Attribute catalog (standard + company-defined custom) for the by-email
  // batch attribute fields. Loaded once.
  useEffect(() => {
    let cancelled = false;
    http_get(API_ENDPOINTS.GET_PERSONALIZATION_ATTRIBUTES)
      .then(({ data }) => { if (!cancelled) setAttrCatalog({ standard: data.standard || [], custom: data.custom || [] }); })
      .catch(() => { if (!cancelled) setAttrCatalog({ standard: [], custom: [] }); });
    return () => { cancelled = true; };
  }, []);

  const settableAttrs = useMemo(() => {
    const std = (attrCatalog.standard || []).filter((a) => !PER_PERSON_KEYS.includes(a.key));
    return [...std, ...(attrCatalog.custom || [])];
  }, [attrCatalog]);

  const attrPayload = useMemo(() => {
    const out = {};
    Object.entries(attrValues).forEach(([k, v]) => {
      const val = String(v ?? '').trim();
      if (val) out[k] = val;
    });
    return out;
  }, [attrValues]);

  const setAttr = (key, value) => setAttrValues((prev) => ({ ...prev, [key]: value }));

  const parsedEmails = useMemo(() => parseEmails(emailsText), [emailsText]);
  const count = tab === 'audience' ? selected.size : parsedEmails.length;
  const countLabel = tab === 'audience' ? `${count} selected` : `${count} email${count === 1 ? '' : 's'}`;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const send = async () => {
    if (count === 0 || !materialId) return;
    setSending(true);
    setError('');
    try {
      const { data, status } = await http_post(API_ENDPOINTS.POST_MATERIAL_INVITES, {
        learning_material_id: materialId,
        audience_ids: tab === 'audience' ? [...selected] : [],
        emails: tab === 'email' ? parsedEmails : [],
        send_branded_email: sendBranded,
        auto_remind: autoRemind,
        personal_message: message,
        attributes: tab === 'email' ? attrPayload : {}
      });
      if (data?.error || status >= 300) { setError(data.error || 'Could not send invites.'); return; }
      const created = (data.created || []).length;
      const skipped = (data.skipped || []).length;
      toast.success(`Sent ${created} invite${created === 1 ? '' : 's'}`);
      if (skipped) toast.info(`${skipped} skipped (already invited)`);
      onSent?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ma-invite-overlay" onClick={onClose}>
      <div className="ma-invite" onClick={(e) => e.stopPropagation()}>
        <div className="im-h">
          <div className="kk">Invite to {kindWord}</div>
          <h4>Add users to “{selectedMaterial?.name || 'this material'}”</h4>
          <p>Send a branded invite by email or pick from your existing audience.</p>
          <span className="x" onClick={onClose}>✕</span>
        </div>

        <div className="im-b">
          <p className="flabel">{kindWord}</p>
          <div className="selcard">
            <span className="mi">▤</span>
            <div className="selcard__info">
              <div className="nm">{selectedMaterial?.name}</div>
              <div className="me">{selectedMaterial?.step_count} {unit} · {selectedMaterial?.users_count} users</div>
            </div>
            <select className="selcard__select" value={materialId || ''} onChange={(e) => setMaterialId(Number(e.target.value))}>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>

          <div className="seg">
            <div className={`s ${tab === 'audience' ? 'on' : ''}`} onClick={() => setTab('audience')}>From audience</div>
            <div className={`s ${tab === 'email' ? 'on' : ''}`} onClick={() => setTab('email')}>By email</div>
          </div>

          {tab === 'audience' ? (
            <>
              <div className="isearchw">
                <span className="mag" />
                <input className="isearch" placeholder="Search audience by name, email, or company…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="chips">
                {SEGMENTS.map((s) => (
                  <span key={s.key} className={`chip ${segment === s.key ? 'on' : ''}`} onClick={() => setSegment(s.key)}>{s.label}</span>
                ))}
              </div>
              <div className="tick">Tick the people you want to invite to this {kindWord}.</div>
              <div className="alist">
                {loadingAudiences ? (
                  <div className="aempty">Loading…</div>
                ) : audiences.length === 0 ? (
                  <div className="aempty">No matching audience members.</div>
                ) : audiences.map((a) => {
                  const isSel = selected.has(a.id);
                  const tg = TG_CLASS[a.segment];
                  return (
                    <div key={a.id} className={`arow ${isSel ? 'sel' : ''}`} onClick={() => toggle(a.id)}>
                      <span className={`chk ${isSel ? 'on' : ''}`}>{isSel ? '✓' : ''}</span>
                      <span className="av" style={{ background: colorFor(a.id) }}>{initialsFor(a)}</span>
                      <div className="arow__body">
                        <div className="nm">
                          {a.learner?.name}
                          {tg && <span className={`tg ${tg}`}>{a.segment}</span>}
                        </div>
                        <div className="em">{a.learner?.email}{a.companyName ? ` · ${a.companyName}` : ''}</div>
                      </div>
                      <div className="rt">{timeAgo(a.recentActivity)}</div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <p className="flabel">Email addresses</p>
              <textarea
                className="paste"
                placeholder="avery@acme.com, casey@acme.com&#10;devon@acme.com"
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
              />
              <div className="tick tick--click" onClick={() => setAlsoAdd((v) => !v)}>
                <span className={`cbmini ${alsoAdd ? 'on' : ''}`}>{alsoAdd ? '✓' : ''}</span>
                Also add these people to my audience
              </div>

              {settableAttrs.length > 0 && (
                <div className="attrbox">
                  <div className="attrbox__h" onClick={() => setShowAttrs((v) => !v)}>
                    <span className="attrbox__tw">Set attributes for these people <span className="attrbox__opt">optional</span></span>
                    <span className="attrbox__chev">{showAttrs ? '▲' : '▼'}</span>
                  </div>
                  {showAttrs && (
                    <>
                      <div className="attrbox__note">Applied to everyone in this invite. Existing people keep the values they already have — only blank fields are filled.</div>
                      <div className="attrgrid">
                        {settableAttrs.map((a) => (
                          <label key={a.key} className="attrfield">
                            <span className="attrfield__l">{a.label}</span>
                            {a.key === 'segment' ? (
                              <select className="attrfield__i" value={attrValues[a.key] || ''} onChange={(e) => setAttr(a.key, e.target.value)}>
                                <option value="">—</option>
                                {SEGMENT_OPTIONS.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                              </select>
                            ) : (
                              <input
                                className="attrfield__i"
                                type={a.kind === 'number' ? 'number' : 'text'}
                                value={attrValues[a.key] || ''}
                                placeholder={`Add ${a.label.toLowerCase()}…`}
                                onChange={(e) => setAttr(a.key, e.target.value)}
                              />
                            )}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          <div className="optbox">
            <div className="oh">Invite options</div>
            <div className="opt" onClick={() => setSendBranded((v) => !v)}>
              <span className={`cb ${sendBranded ? 'on' : ''}`}>{sendBranded ? '✓' : ''}</span>
              <div><div className="ot">Send branded invite email</div><div className="od">Includes a magic link, your logo, and your custom welcome copy.</div></div>
            </div>
            <div className="opt" onClick={() => setAutoRemind((v) => !v)}>
              <span className={`cb ${autoRemind ? 'on' : ''}`}>{autoRemind ? '✓' : ''}</span>
              <div><div className="ot">Auto-remind if not started in 3 days</div><div className="od">One nudge email. Cancels automatically once they begin.</div></div>
            </div>
          </div>

          <div className="msg">
            <p className="flabel" style={{ marginBottom: 6 }}>Optional message</p>
            <textarea placeholder="Add a personal note…" value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>

          {error && <div className="im-err">{error}</div>}
        </div>

        <div className="im-f">
          <span className="cnt">{countLabel}</span>
          <button className="btn btn-primary btn-sm" disabled={count === 0 || sending} onClick={send} type="button">
            {sending ? 'Sending…' : `Send ${count} invite${count === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteModal;
